import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaMedium } from 'react-icons/fa';
import { useMediumArticles, resolveImagePath } from '../../hooks/useMediumArticles';
import { CATEGORIES, inferCategories } from '../../constants/articleCategories';

interface ArticleImageProps {
  src?: string;
  alt: string;
}

const ArticleImage: React.FC<ArticleImageProps> = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  const resolvedSrc = resolveImagePath(src);

  if (!resolvedSrc || hasError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 text-gray-400 p-6 text-center select-none">
        <span className="text-4xl mb-2">📝</span>
        <span className="text-xs font-bold uppercase tracking-widest text-[#05a5b7]">Christ Reigns Media</span>
      </div>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      onError={() => setHasError(true)}
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      loading="lazy"
    />
  );
};

/**
 * ArticlesList
 * Renders the scraped Medium article grid with:
 *  - Live search bar (title + subtitle)
 *  - Colorful category pill filters (weighted keyword engine in articleCategories.ts)
 *  - Language tab filter (All / EN / ES)
 */
export const ArticlesList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language.startsWith('es');

  // ── Language tab ────────────────────────────────────────────────────────
  const getInitialTab = (): 'all' | 'en' | 'es' =>
    i18n.language.startsWith('es') ? 'es' : 'en';

  const [activeTab, setActiveTab] = useState<'all' | 'en' | 'es'>(getInitialTab());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());

  const { articles, loading, error, refetch } = useMediumArticles();
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const apiBase =
        process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'production'
          ? 'https://christreignsmedia-api-b91e81f2bbea.herokuapp.com'
          : 'http://localhost:3001');

      // Trigger sync
      await fetch(`${apiBase}/articles/sync`, { method: 'POST' });
      
      // Wait a short time for sync to start, then refetch
      setTimeout(async () => {
        await refetch();
        setSyncing(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to trigger sync:', err);
      setSyncing(false);
    }
  };

  // Sync language tab when site language changes
  useEffect(() => {
    setActiveTab(i18n.language.startsWith('es') ? 'es' : 'en');
  }, [i18n.language]);

  // Date formatter
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } catch {
      return dateStr;
    }
  };

  // Toggle category pill
  const toggleCategory = (id: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Filtered article list ───────────────────────────────────────────────
  const filteredArticles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return articles.filter((article) => {
      if (activeTab !== 'all' && article.language !== activeTab) return false;
      if (q) {
        const text = `${article.title} ${article.subtitle || ''}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      if (activeCategories.size > 0) {
        const cats = inferCategories(article.title, article.subtitle || '');
        if (!cats.some((c) => activeCategories.has(c))) return false;
      }
      return true;
    });
  }, [articles, activeTab, searchQuery, activeCategories]);

  // ── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[400px]">
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes spin-reverse { 0% { transform: rotate(360deg); } 100% { transform: rotate(0deg); } }
          .custom-spinner-outer { animation: spin-slow 1.5s linear infinite; }
          .custom-spinner-inner { animation: spin-reverse 1s linear infinite; }
        ` }} />
        <div className="relative flex items-center justify-center w-20 h-20">
          <div className="custom-spinner-outer absolute inset-0 rounded-full border-4 border-transparent border-t-[#05a5b7] border-b-[#05a5b7]/40" />
          <div className="custom-spinner-inner absolute w-12 h-12 rounded-full border-4 border-transparent border-r-blue-500 border-l-blue-500/40" />
          <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-[#05a5b7] to-blue-500 animate-pulse" />
        </div>
        <span className="mt-6 text-gray-500 text-xs font-bold tracking-widest uppercase animate-pulse">
          {t('articles.loading')}
        </span>
      </div>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="py-16 max-w-2xl mx-auto px-4 text-center">
        <div className="bg-red-50 border border-red-100 rounded-3xl p-8 shadow-sm">
          <span className="text-4xl block mb-4" role="img" aria-label="warning">⚠️</span>
          <h3 className="text-lg font-bold text-red-800 mb-2">
            {isEs ? 'Oops! Algo salió mal' : 'Oops! Something went wrong'}
          </h3>
          <p className="text-red-600 text-sm mb-6">
            {isEs
              ? 'No pudimos cargar los artículos de Medium en este momento. Por favor, inténtalo de nuevo.'
              : 'We couldn\'t load the Medium articles at this time. Please try again.'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-6 py-2.5 bg-[#05a5b7] hover:bg-[#048a99] text-white rounded-full text-sm font-semibold transition-colors shadow-md"
          >
            {isEs ? 'Intentar de nuevo' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────
  return (
    <div className="py-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Search Bar */}
      <div className="relative mb-8 max-w-2xl mx-auto">
        <input
          id="article-search"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={isEs ? 'Buscar artículos...' : 'Search articles...'}
          className="w-full pl-6 pr-14 py-4 text-base text-gray-800 bg-white border-2 border-gray-200 rounded-full shadow-sm outline-none focus:border-[#05a5b7] focus:shadow-[0_0_0_4px_rgba(5,165,183,0.12)] transition-all duration-200 placeholder-gray-400"
        />
        <span className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
          </svg>
        </span>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
            className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Category Pills */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 leading-none">
              {isEs ? 'Categorías' : 'Categories'}
            </h3>
            <div className="mt-1 w-10 h-0.5 bg-[#05a5b7] rounded-full" />
          </div>
          {activeCategories.size > 0 && (
            <button
              onClick={() => setActiveCategories(new Set())}
              className="ml-auto text-xs text-gray-400 hover:text-gray-700 border border-gray-200 rounded-full px-3 py-1 transition-colors hover:bg-gray-50"
            >
              {isEs ? 'Limpiar filtros' : 'Clear filters'}
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((cat) => {
            const isActive = activeCategories.has(cat.id);
            return (
              <button
                key={cat.id}
                onClick={() => toggleCategory(cat.id)}
                style={{
                  backgroundColor: isActive ? cat.color : `${cat.color}22`,
                  color: isActive ? '#ffffff' : cat.color,
                  borderColor: `${cat.color}55`,
                  transform: isActive ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isActive ? `0 4px 14px ${cat.color}55` : 'none',
                }}
                className="px-4 py-2 rounded-xl text-sm font-bold border transition-all duration-200 hover:scale-105 hover:shadow-md select-none"
              >
                {isEs ? cat.labelEs : cat.labelEn}
              </button>
            );
          })}
        </div>
      </div>

      {/* Language Tab + Count Row */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 border-b border-gray-100 pb-6 gap-4">
        <div className="flex space-x-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-150">
          {(['all', 'en', 'es'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                activeTab === tab
                  ? 'bg-[#05a5b7] text-white shadow-md shadow-[#05a5b7]/20'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              {tab === 'all' ? t('articles.tabAll') : tab === 'en' ? t('articles.tabEn') : t('articles.tabEs')}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-4">
          <span className="text-xs bg-[#05a5b7]/10 text-[#05a5b7] font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
            {filteredArticles.length} {t('articles.totalCount')}
          </span>
          <button
            onClick={handleSync}
            disabled={loading || syncing}
            className="text-xs font-bold text-gray-500 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 uppercase tracking-wider"
          >
            {loading || syncing ? (isEs ? 'Sincronizando...' : 'Syncing...') : t('articles.syncFeed')}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {filteredArticles.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 border border-dashed border-gray-200 rounded-3xl">
          <span className="text-5xl block mb-4" role="img" aria-label="empty">🔍</span>
          <h3 className="text-lg font-bold text-gray-800 mb-2">
            {isEs ? 'No se encontraron artículos' : 'No articles found'}
          </h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            {isEs
              ? 'Intenta con otra búsqueda o selecciona una categoría diferente.'
              : 'Try a different search term or select a different category.'}
          </p>
          {(searchQuery || activeCategories.size > 0) && (
            <button
              onClick={() => { setSearchQuery(''); setActiveCategories(new Set()); }}
              className="mt-6 px-6 py-2.5 bg-[#05a5b7] text-white rounded-full text-sm font-semibold hover:bg-[#048a99] transition-colors shadow"
            >
              {isEs ? 'Restablecer filtros' : 'Reset Filters'}
            </button>
          )}
        </div>
      ) : (
        /* Article Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArticles.map((article) => {
            const cats = inferCategories(article.title, article.subtitle || '');
            const primaryCat = CATEGORIES.find((c) => c.id === cats[0]);

            return (
              <Link
                key={article.id}
                to={`/articles/${article.id}`}
                className="flex flex-col bg-white border border-gray-250/60 rounded-3xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-[#05a5b7]/15 hover:border-[#05a5b7]/80 group h-full shadow-md"
              >
                {/* Cover Image */}
                <div className="relative aspect-video w-full overflow-hidden bg-gray-50">
                  <ArticleImage src={article.imageUrl} alt={article.title} />

                  {/* Language badge */}
                  <span className="absolute top-4 right-4 z-10 px-2.5 py-1 text-[10px] font-bold tracking-widest text-white uppercase bg-black/60 backdrop-blur-md rounded-md">
                    {article.language}
                  </span>

                  {/* Category badge */}
                  {primaryCat && (
                    <span
                      className="absolute top-4 left-4 z-10 px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase rounded-md text-white"
                      style={{ backgroundColor: primaryCat.color }}
                    >
                      {isEs ? primaryCat.labelEs : primaryCat.labelEn}
                    </span>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Card Body */}
                <div className="p-6 flex flex-col flex-grow">
                  {article.publishedAt && (
                    <span className="text-xs text-[#05a5b7] font-bold uppercase tracking-wider font-mono mb-3 block">
                      {formatDate(article.publishedAt)}
                    </span>
                  )}

                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 leading-snug group-hover:text-[#05a5b7] transition-colors">
                    {article.title}
                  </h3>

                  {article.subtitle && (
                    <p className="text-gray-600 text-sm mb-6 line-clamp-3 leading-relaxed flex-grow">
                      {article.subtitle}
                    </p>
                  )}

                  <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="inline-flex items-center text-sm font-semibold text-[#05a5b7] transition-colors group-hover:text-blue-600">
                      {isEs ? 'Ver Artículo' : 'View Article'}
                      <svg className="w-4 h-4 ml-1.5 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </span>
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition-all"
                      title={isEs ? 'Leer en Medium' : 'Read on Medium'}
                    >
                      <FaMedium className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* View More on Medium Section */}
      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 border-t border-gray-100 pt-8">
        {(activeTab === 'all' || activeTab === 'en') && (
          <a
            href="https://medium.com/@christreignsmedia"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 border-2 border-[#05a5b7] text-[#05a5b7] font-bold rounded-full hover:bg-[#05a5b7] hover:text-white transition-all duration-200 text-sm uppercase tracking-wider text-center min-w-[200px]"
          >
            {isEs ? 'Ver más en Medium (EN)' : 'View More on Medium (EN)'}
          </a>
        )}
        {(activeTab === 'all' || activeTab === 'es') && (
          <a
            href="https://medium.com/@christ-reigns-espanol"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 border-2 border-[#05a5b7] text-[#05a5b7] font-bold rounded-full hover:bg-[#05a5b7] hover:text-white transition-all duration-200 text-sm uppercase tracking-wider text-center min-w-[200px]"
          >
            {isEs ? 'Ver más en Medium (ES)' : 'View More on Medium (ES)'}
          </a>
        )}
      </div>
    </div>
  );
};

export default ArticlesList;
